import type { KPITrend } from '../components/KPICard/KPICard';

export function kpiTrend(
  current: number | null | undefined,
  prior: number | null | undefined
): KPITrend | null {
  if (current === null || current === undefined || prior === null || prior === undefined) {
    return null;
  }
  const delta = current - prior;
  if (delta === 0) return null;
  const pct = prior !== 0 ? (Math.abs(delta) / Math.abs(prior)) * 100 : 100;
  return {
    direction: delta > 0 ? 'up' : 'down',
    value: `${pct.toFixed(0)}%`,
  };
}

export function invertKpiDirection(
  trend: KPITrend | null
): KPITrend | null {
  if (!trend) return null;
  return {
    ...trend,
    direction: trend.direction === 'up' ? 'down' : 'up',
  };
}

/** Share delta in percentage points, e.g. "+5 pts vs previous 7 days". */
export function kpiPointsDelta(
  current: number | null | undefined,
  prior: number | null | undefined,
  windowDays: number
): KPITrend | null {
  if (current === null || current === undefined || prior === null || prior === undefined) {
    return null;
  }
  const delta = (current - prior) * 100;
  if (Math.abs(delta) < 0.5) return null;
  const signed = `${delta > 0 ? '+' : ''}${delta.toFixed(0)}`;
  return {
    direction: delta > 0 ? 'up' : 'down',
    value: `${signed} pts vs previous ${windowDays} days`,
  };
}

/** Absolute delta vs an equal-length previous window, e.g. "+0.4 vs previous 7 days". */
export function kpiAbsoluteDelta(
  current: number | null | undefined,
  prior: number | null | undefined,
  windowDays: number,
  digits = 1
): KPITrend | null {
  if (current === null || current === undefined || prior === null || prior === undefined) {
    return null;
  }
  const delta = current - prior;
  if (delta === 0) return null;
  const signed = `${delta > 0 ? '+' : ''}${delta.toFixed(digits)}`;
  return {
    direction: delta > 0 ? 'up' : 'down',
    value: `${signed} vs previous ${windowDays} days`,
  };
}

export function formatScore(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toFixed(2);
}

export function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(0)}%`;
}

export function formatCredit(value: number): string {
  const body = value.toFixed(2);
  if (value > 0) return `+${body}`;
  return body;
}

export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return Math.round(value).toLocaleString();
}

export function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export const RANGE_PRESETS = [7, 30, 90] as const;
export type RangePresetDays = (typeof RANGE_PRESETS)[number];

export function defaultRangeDays(days: number = 30, todayIso?: string): { start: string; end: string } {
  const end = todayIso ? new Date(`${todayIso}T12:00:00.000Z`) : new Date();
  const start = new Date(end.getTime());
  start.setUTCDate(end.getUTCDate() - (days - 1));
  return { start: isoDate(start), end: isoDate(end) };
}

export function matchingRangePreset(
  start: string,
  end: string,
  todayIso: string = isoDate(new Date())
): RangePresetDays | null {
  for (const days of RANGE_PRESETS) {
    const range = defaultRangeDays(days, todayIso);
    if (range.start === start && range.end === end) return days;
  }
  return null;
}

export function formatRangeLabel(start: string, end: string): string {
  const fmt = (iso: string) => {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  return `${fmt(start)} – ${fmt(end)}`;
}
